import {guid} from "../../../Util/guid";
import {brickDefinitions} from "./brick-definitions";
import brickTypes from './brick-types';
import brickInitializers from './brick-initializers';

const deepClone = (obj) => {
    //Null, undefined, etc. can't be serialized
    //thus we can simply return the original value
    if(!obj){
        return obj;
    }
    
    //Hacky, but works
    return JSON.parse(JSON.stringify(obj));
};

const createJourneyBrick = (brickDefinition, config) => {
    return createJourneyBrickFromModel({
        id: guid(),
        name: brickDefinition.displayName,
        type: brickDefinition.type,
        config: JSON.stringify(config || {}),
        isNew: true
    });
};

const createJourneyBrickFromModel = (remoteModel) => {
    const brick = {
        id: remoteModel.id,
        name: remoteModel.name,
        type: remoteModel.type,
        config: JSON.parse(remoteModel.config),
        connections: [],
        x: remoteModel.x || 0,
        y: remoteModel.y || 0,
        isNew: remoteModel.isNew || false,
        get isChildJourneyBrick(){
            return this.type === brickTypes.journey && this.config.isChildJourney === true;
        },
        get outgoingConnections() {
            return this.connections.filter(r => r.sourceBrickId === this.id);
        },
        get incomingConnections() {
            return this.connections.filter(r => r.destinationBrickId === this.id);
        },
        deregisterConnection(connection){
            this.connections.splice(
                this.connections.indexOf(connection), 
                1
            );
        },
        registerConnection(connection){
            if(this.connections.indexOf(connection) === -1){
                this.connections.push(connection);
            }
        },
        addConnection(){
            return createJourneyBrickConnectionFromModel({
                id: guid()
            }, this);
        }
    };

    //We should only set outgoing connections on initialize
    (remoteModel.connections || [])
        .filter(cm => cm.sourceBrickId === brick.id)
        .forEach(cm => createJourneyBrickConnectionFromModel(cm, brick));

    //Certain brick types require special actions/events, those will be executed/set in our initializer
    const initializer = brickInitializers[brick.type];
    if(initializer){
        initializer(brick);
    }
    
    return brick;
};

const createJourneyBrickConnectionFromModel = (remoteModel, sourceBrick) => {
    const config = remoteModel.config ? JSON.parse(remoteModel.config) : {};

    config.delay = config.delay || {};
    
    let connectionDestinationBrick = undefined;
    
    const connection = {
        id: remoteModel.id,
        name: remoteModel.name,

        conditions: remoteModel.conditions || [],

        connectorX: remoteModel.connectorX || 0,
        connectorY: remoteModel.connectorY || 0,

        isDisabled: remoteModel.isDisabled || false,
        startDate: remoteModel.startDate || null,

        config: config,
        
        delete(){
            const { sourceBrick, destinationBrick } = this;
            
            //deregisterConnection should be called on both bricks
            //this to ensure any custom handlers get triggered
            sourceBrick.deregisterConnection(this);
            if(destinationBrick) destinationBrick.deregisterConnection(this);
        }
    };

    //https://stackoverflow.com/a/34890593
    //Setting enumerable to false for our Source/Target bricks
    //prevents them from showing up in enumerations; and
    //thus serialization
    Object.defineProperties(connection, {
        //Purely local
        ui: {
            enumerable: false,
            writable: true,
            value: {
                color: undefined,
                tooltip: {
                    title: undefined,
                    content: undefined
                }
            }
        },
        
        //Source is a static value, and will not change
        //thus we can simply make our source non-writeable
        sourceBrick: {
            enumerable: false,
            writable: false,
            value: sourceBrick
        },
        sourceBrickId: {
            enumerable: true,
            writable: false,
            value: sourceBrick.id
        },
        
        //Destination is not guaranteed to be available upon connection creation
        destinationBrick: {
            enumerable: false,
            set(value) {
                //Remove connection from previous brick
                if(connectionDestinationBrick && (!value || connectionDestinationBrick.id !== value.id)){
                    connectionDestinationBrick.deregisterConnection(this);
                }
                
                //Add connection to new brick
                if(value && (!connectionDestinationBrick || connectionDestinationBrick.id !== value.id)){
                    value.registerConnection(this);
                }

                connectionDestinationBrick = value;
            },
            get: () => connectionDestinationBrick
        },
        destinationBrickId: {
            enumerable: true,
            get: () => connectionDestinationBrick && connectionDestinationBrick.id
        }
    });
    
    sourceBrick.registerConnection(connection);
    
    return connection;
};


const createFactory = (typeConfigMapping) => {
    //Type configs are remote defined
    return {
        createNewJourneyBrick: (type) => {
            const config = typeConfigMapping[type];

            return createJourneyBrick(
                brickDefinitions[type],
                deepClone(config)
            );      
        },
        createJourneyBrickFromModel: (remoteModel) => {
            return createJourneyBrickFromModel(remoteModel);
        }
    };
};

export default createFactory;